import {
  AdornedInput,
  Adornment,
  Alert,
  DatePicker,
  DatePickerInput,
  FieldSet,
  FormField,
  FormHelp,
  IconCalendarOutline,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  Spacer,
} from '@zillow/constellation';
import { LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../../constants/stringConstants';

import type { Lease } from '../../../../models';
import type { TerminationReason, TerminationReasonDto } from '@zg-rentals/ts-leasing-client';

type OwnProps = {
  activeCancelationReasons: Array<TerminationReasonDto>;
  canceledDate: string;
  cancellationReason: TerminationReason;
  onCanceledDateChange(date: string): void;
  onCancellationReasonChange(cancellationReason: TerminationReason): void;
  lease: Lease;
  isCanceledDateOutOfRange(date: Date): boolean;
  isCanceledDateInError: boolean;
  canceledDateError: string;
  isLoadingReasons: boolean;
};

export default function CancellationPrompt({
  activeCancelationReasons,
  canceledDate,
  cancellationReason,
  onCanceledDateChange,
  onCancellationReasonChange,
  lease,
  isCanceledDateOutOfRange,
  isCanceledDateInError,
  canceledDateError,
  isLoadingReasons,
}: OwnProps) {
  function onHandleCancellationReasonChange(e: React.ChangeEvent) {
    const { id } = e.currentTarget;

    onCancellationReasonChange(id as TerminationReason);
  }

  return (
    <>
      {lease?.latestExtension && (
        <Spacer marginBottom="sm">
          <Alert
            appearance="warning"
            body="By canceling your lease, all lease renewal agreements will also be canceled"
          />
        </Spacer>
      )}
      <Paragraph marginBottom="sm">
        Canceling this lease does not represent a legal termination or eviction. You will still need to provide the
        tenant with a legal form of termination.
      </Paragraph>
      <FormField
        required
        marginBottom="sm"
        error={isCanceledDateInError}
        label={<Label aria-label="cancelation date">Canceled date</Label>}
        control={
          <AdornedInput
            input={
              <DatePickerInput
                placeholder={DATE_PICKER_PLACEHOLDER}
                value={canceledDate}
                onChange={onCanceledDateChange}
                datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={isCanceledDateOutOfRange} />}
              />
            }
            rightAdornment={
              <Adornment>
                <IconCalendarOutline />
              </Adornment>
            }
          />
        }
        description={isCanceledDateInError && <FormHelp>{canceledDateError}</FormHelp>}
      />
      {isLoadingReasons ? (
        <>
          <LoadingHeading isLoading level={2} loadingWidth="50%">
            Why is the lease being canceled?
          </LoadingHeading>
          <Spacer marginTop="xs">
            {Array.from({ length: 6 }).map((_, index) => (
              <Spacer key={`skeleton-${index}`} marginBottom="sm">
                <LoadingText isLoading loadingWidth="50%">
                  Loading reason
                </LoadingText>
              </Spacer>
            ))}
          </Spacer>
        </>
      ) : (
        <FieldSet required legend={<Legend>Why is the lease being canceled?</Legend>}>
          {activeCancelationReasons.map((option) => (
            <LabeledControl
              key={option.name}
              controlId={option.name}
              label={<Label>{option.label}</Label>}
              control={
                <Radio
                  name="cancellationReason"
                  checked={cancellationReason === option.name}
                  onChange={onHandleCancellationReasonChange}
                />
              }
            />
          ))}
        </FieldSet>
      )}
    </>
  );
}
